import React from 'react';
import { FormattedMessage } from 'react-intl';

// Components
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import ProgramPassbookRate from '../ProgramPassbookRate';
import SetupGridItem from '../../../SetupGridItem';
import {
  AnnualRecertMonthPicker,
  SetupLabel,
  SetupSwitch,
  SetupValue,
} from '../shared';
import OnOffTypography from '../../../OnOffTypography';
import PropertyStateConfig from '../../../PropertyStateConfig';
import CertificationPastDue from '../../components/CertificationPastDue';
import RecertType from '../../components/RecertType';

import type { HOMEValues, PropertyStateConfigValues } from '../../../types';

import { useHOME } from './hooks';
import messages from './messages';
import messagesAffordableSetup from '../../messages';

interface HomeFlags {
  passbookRate: boolean;
}

type HomeSetupValues = HOMEValues & {
  flags: HomeFlags,
};

type Props = {
  values: HomeSetupValues,
} & PropertyStateConfigValues;

const HomeSetup = ({
  editMode,
  isLoadingPassbookRateData,
  onChange,
  promptToaster,
  values: { flags, passbookRates, hotmaConfig, ...values },
  state,
  stateConfigs,
}: Props) => {
  const { ReactHookFormProps, memoizedFields } = useHOME({
    editMode,
    onChange,
    values,
  });

  const handleHOMEAnnualRecertMonthChange = (
    date: Object,
    validate: Object,
  ) => {
    onChange({
      target: { name: 'setup.HOMEAnnualRecertMonth', value: date },
    });
  };

  return (
    <>
      <Form
        ReactHookFormProps={ReactHookFormProps}
        header={
          <Typography variant={'titleMedium'}>
            <FormattedMessage {...messages.title} />
          </Typography>
        }
        fields={memoizedFields}
      >
        <Grid container>
          <RecertType
            editMode={editMode}
            program="HOME"
            recertTypes={values.recertTypes}
            onChange={onChange}
          />
        </Grid>

        <Grid container>
          <Grid item xs={12} sm={6} />
          <SetupGridItem
            label={
              <FormattedMessage
                {...messagesAffordableSetup.autoRolloverIncomeAndAssets}
              />
            }
            TooltipProps={{
              title: (
                <FormattedMessage
                  {...messagesAffordableSetup.autoRolloverIncomeAndAssetsDescription}
                />
              ),
            }}
          >
            <SetupSwitch
              switchId={'home-auto-rollover-income-and-assets-switch'}
              editMode={editMode}
              name="setup.HOMEAutoRolloverIncomeAndAssets"
              disabled={!editMode}
              value={values.autoRolloverIncomeAndAssets}
              onChange={onChange}
            />
          </SetupGridItem>
        </Grid>

        <Grid container>
          <SetupGridItem
            label={
              <FormattedMessage
                {...messagesAffordableSetup.annualRecertMonth}
                values={{ program: 'HOME' }}
              />
            }
          >
            <AnnualRecertMonthPicker
              handleAnnualRecertMonthChange={handleHOMEAnnualRecertMonthChange}
              editMode={editMode}
              programName={'HOME'}
              value={values.HOMEAnnualRecertMonth}
            />
          </SetupGridItem>

          <PropertyStateConfig
            editMode={editMode}
            onChange={onChange}
            state={state}
            stateConfigs={stateConfigs}
            affordableProgramName={'HOME'}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messagesAffordableSetup.hotmaActive} />
              <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
                {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                  <FormattedMessage {...messagesAffordableSetup.on} />
                ) : (
                  <FormattedMessage {...messagesAffordableSetup.off} />
                )}
              </OnOffTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetupLabel
                {...messagesAffordableSetup.hotmaImplementationDate}
              />
              <SetupValue
                name="HOME.HOTMAEffectiveDate"
                value={
                  hotmaConfig?.hasAdoptedHOTMA ?? false
                    ? hotmaConfig?.effectiveDate
                    : '---'
                }
                editable={false}
              />
            </Grid>
            <CertificationPastDue
              editMode={editMode}
              value={values.certificationPastDueDefinition}
              name="setup.HOMECertificationPastDueDefinition"
              onChange={onChange}
            />
          </Grid>
        </Grid>

        {flags.passbookRate && (
          <ProgramPassbookRate
            key={'HOME_PassbookRates'}
            programName={'HOME'}
            isLoadingPassbookRateData={isLoadingPassbookRateData}
            passbookRates={passbookRates}
            sx={{ paddingTop: 1, paddingRight: 0, paddingLeft: 0 }}
          />
        )}
      </Form>
    </>
  );
};

export default HomeSetup;
